import PropTypes from "prop-types"
import React, { useState } from "react"

import { sanitize } from "../../../utils/utils"

const ServicePractitioner = ({ practice, theme }) => {
  const [showFullBio, setShowFullBio] = useState(false)

  // Get practitioner image with fallbacks
  const getPractitionerImage = () => {
    // First try user profile photo (most common for practitioner images)
    if (practice.user?.profilePhotoUrl) {
      return practice.user.profilePhotoUrl
    }

    // Then try practitioner image
    if (practice.practitionerImage) {
      // Check if it's a filestack handle (just a string without http)
      if (practice.practitionerImage && !practice.practitionerImage.startsWith("http")) {
        const imageUrl = `https://cdn.filestackcontent.com/${practice.practitionerImage}` // eslint-disable-line no-console
        return imageUrl
      }
      return practice.practitionerImage
    }

    // Then try first gallery image
    if (practice.galleryImages && practice.galleryImages.length > 0) {
      const firstImage = practice.galleryImages[0]
      if (firstImage.filestackPhoto?.large?.webp) {
        const imageUrl = `https://cdn.filestackcontent.com/${firstImage.filestackPhoto.large.webp}`
        return imageUrl
      }
      if (firstImage.url) {
        return firstImage.url
      }
    }

    // Finally try image assets for a profile image
    if (practice.imageAssets) {
      const profileImage = practice.imageAssets.find(
        (asset) =>
          asset.description === "profile" || asset.description === "avatar" || asset.description === "practitioner"
      )
      if (profileImage?.resizedImageUrl) {
        return profileImage.resizedImageUrl
      }
    }

    console.warn("No image found, returning null") // eslint-disable-line no-console
    return null
  }

  // Get practitioner data with real review information
  const practitionerData = {
    name: practice.practitionerName || practice.name,
    bio: practice.about || practice.practitionerBio || "",
    image: getPractitionerImage(),
    rating: practice.reviewsAverage || practice.averageRating || practice.rating || 0,
    reviewCount: practice.reviewsCount || practice.reviewCount || 0
  }

  const truncateBio = (text, limit = 300) => {
    if (!text) return ""
    if (text.length <= limit) return text
    return text.substring(0, limit) + "..."
  }

  const displayBio = showFullBio ? practitionerData.bio : truncateBio(practitionerData.bio, 300)

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating)
    const hasHalfStar = rating % 1 !== 0
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0)

    return (
      <div className="flex h-5 items-center justify-start gap-0.5">
        {/* Full stars */}
        {[...Array(fullStars)].map((_, i) => (
          <div key={`full-${i}`} className="h-5 w-5" style={{ color: theme?.colors?.reviewStars || "#ff9529" }}>
            ★
          </div>
        ))}

        {/* Half star */}
        {hasHalfStar && (
          <div className="relative h-5 w-5" style={{ color: theme?.colors?.reviewStars || "#ff9529" }}>
            <span className="absolute inset-0">☆</span>
            <span className="absolute inset-0 w-1/2 overflow-hidden">★</span>
          </div>
        )}

        {/* Empty stars */}
        {[...Array(emptyStars)].map((_, i) => (
          <div key={`empty-${i}`} className="h-5 w-5" style={{ color: "#e5e5e5" }}>
            ☆
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col items-start justify-start gap-10">
      <div className="flex w-full flex-col items-start justify-start gap-6">
        <div className="flex flex-col items-start justify-center gap-8">
          {/* Practitioner Image */}
          <div
            className="h-56 w-56 rounded-xl bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: practitionerData.image ? `url(${practitionerData.image})` : "none",
              backgroundColor: practitionerData.image ? "transparent" : theme?.colors?.greyLight || "#e5e5e5"
            }}>
            {!practitionerData.image && (
              <div className="flex h-full w-full items-center justify-center">
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-300">
                  <svg className="h-12 w-12 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Practitioner Info */}
          <div className="flex flex-col items-start justify-start gap-3">
            <div
              className="flex flex-col items-start justify-start"
              style={{ color: theme?.colors?.textDark || "#55433f" }}>
              <div
                className="text-[18px] font-normal leading-[32px]"
                style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                Meet the Practitioner
              </div>
              <div
                className="text-[28px] font-bold leading-[40px]"
                style={{ fontFamily: theme?.fonts?.heading || "Playfair Display, serif" }}>
                {practitionerData.name}
              </div>
            </div>

            {/* Rating */}
            <div className="flex items-center justify-center gap-2">
              {renderStars(practitionerData.rating)}
              <div
                className="text-[18px] font-normal leading-[32px]"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: theme?.colors?.textDark || "#55433f"
                }}>
                {practitionerData.reviewCount} reviews
              </div>
            </div>
          </div>
        </div>

        {/* Bio */}
        {practitionerData.bio && (
          <div
            className="w-full text-[18px] font-normal leading-[32px]"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              color: theme?.colors?.textDark || "#55433f"
            }}>
            <div
              className="whitespace-pre-line"
              dangerouslySetInnerHTML={{
                __html: sanitize(displayBio)
              }}
            />
            {practitionerData.bio.length > 300 && (
              <button
                onClick={() => setShowFullBio(!showFullBio)}
                className="font-semibold underline decoration-solid transition-colors hover:no-underline"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: theme?.colors?.primary || "#bd654f",
                  borderRadius: theme?.buttonShape?.borderRadius || "0.375rem"
                }}>
                {showFullBio ? "Show less" : "Read more"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

ServicePractitioner.propTypes = {
  practice: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    practitionerName: PropTypes.string,
    practitionerBio: PropTypes.string,
    practitionerImage: PropTypes.string,
    about: PropTypes.string,
    reviewsAverage: PropTypes.number,
    reviewsCount: PropTypes.number,
    averageRating: PropTypes.number,
    rating: PropTypes.number,
    reviewCount: PropTypes.number,
    user: PropTypes.shape({
      profilePhotoUrl: PropTypes.string
    }),
    galleryImages: PropTypes.arrayOf(
      PropTypes.shape({
        filestackPhoto: PropTypes.shape({
          large: PropTypes.shape({
            webp: PropTypes.string
          })
        }),
        url: PropTypes.string
      })
    ),
    imageAssets: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string,
        resizedImageUrl: PropTypes.string
      })
    )
  }).isRequired,
  theme: PropTypes.object
}

export default ServicePractitioner
